/**
 * SourceCard — "How did this bill arrive?" radio-card picker.
 *
 * Replaces the previous horizontal-tab UI with a 3-up grid of selectable
 * cards. Each card has icon + label + 1-line description + active ring
 * + check badge. Mobile collapses to 2-up. Uses semantic `<input
 * type="radio">` for keyboard + screen-reader fidelity.
 *
 * Source modes drive how the bill is routed:
 *
 *   - upload      → operator picks a PDF/photo; the AI extractor pre-fills
 *                   the form (Phase B).
 *   - email       → bills forwarded to the tenant's `bills+*@` address
 *                   land here as drafts automatically (Phase C).
 *   - manual      → operator types the bill from scratch.
 *
 * The body below the picker is mode-specific and rendered by the parent.
 * This file is presentation-only; the parent owns extraction state.
 */
import * as React from "react";
import { Check, Mail, PencilLine, Sparkles, Upload } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { BillSourceMode } from "./types.js";

interface SourceModeOption {
  value: BillSourceMode;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Optional tag shown next to the label (e.g. "AI"). */
  badge?: { label: string; tone: "ai" | "beta" };
}

const SOURCE_MODES: ReadonlyArray<SourceModeOption> = [
  {
    value: "upload",
    label: "Upload PDF/photo",
    description: "Flint extracts vendor, totals, lines.",
    icon: Upload,
    badge: { label: "AI", tone: "ai" },
  },
  {
    value: "email",
    label: "Email forward",
    description: "Forward bills to your inbox address.",
    icon: Mail,
  },
  {
    value: "manual",
    label: "Manual entry",
    description: "Type the bill from scratch.",
    icon: PencilLine,
  },
];

const TONE_BADGE: Record<NonNullable<SourceModeOption["badge"]>["tone"], string> = {
  ai: "bg-sky-100 text-sky-700 border-sky-200",
  beta: "bg-amber-50 text-amber-700 border-amber-200",
};

interface SourceCardProps {
  mode: BillSourceMode;
  onModeChange: (mode: BillSourceMode) => void;
  /** Mode-specific pane rendered by the parent (extraction preview,
   *  template picker, manual hint, etc.). */
  children?: React.ReactNode;
}

export function SourceCard({ mode, onModeChange, children }: SourceCardProps) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-card">
      <div className="h-1 bg-sky-600" />
      <header className="px-4 py-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
          <Upload className="h-4 w-4 text-sky-700" aria-hidden />
          How did this bill arrive?
        </h2>
        <p className="mt-1 text-sm text-muted">
          Pick the source — we route accordingly. AI extraction runs free for the first 50
          bills/month.
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label="Bill source"
        className="grid grid-cols-2 gap-2.5 border-t border-line bg-raised p-3 md:grid-cols-3"
      >
        {SOURCE_MODES.map((opt) => {
          const Icon = opt.icon;
          const active = opt.value === mode;
          const id = `bill-source-${opt.value}`;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={[
                "group relative flex cursor-pointer flex-col gap-2 rounded-md border-2 bg-paper p-3 transition-colors",
                active
                  ? "border-sky-600 bg-sky-50 shadow-[0_0_0_3px_rgba(14,165,233,0.12)]"
                  : "border-line hover:border-line-2 hover:bg-bg",
              ].join(" ")}
            >
              <input
                id={id}
                type="radio"
                name="bill-source-mode"
                value={opt.value}
                checked={active}
                onChange={() => onModeChange(opt.value)}
                className="sr-only"
              />

              {/* Active check badge — sits in the top-right corner */}
              {active ? (
                <span
                  aria-hidden
                  className="absolute right-2 top-2 grid size-5 place-items-center rounded-full bg-sky-600 text-paper"
                >
                  <Check size={12} strokeWidth={3} />
                </span>
              ) : null}

              <div
                className={[
                  "grid size-9 place-items-center rounded-md transition-colors",
                  active ? "bg-sky-600 text-paper" : "bg-raised text-ink-2 group-hover:bg-paper",
                ].join(" ")}
              >
                <Icon size={18} strokeWidth={2} aria-hidden />
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span
                    className={[
                      "text-sm font-semibold leading-tight",
                      active ? "text-sky-900" : "text-ink",
                    ].join(" ")}
                  >
                    {opt.label}
                  </span>
                  {opt.badge ? (
                    <span
                      className={[
                        "inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider",
                        TONE_BADGE[opt.badge.tone],
                      ].join(" ")}
                    >
                      {opt.badge.tone === "ai" ? (
                        <Sparkles size={9} strokeWidth={2.4} aria-hidden />
                      ) : null}
                      {opt.badge.label}
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 text-xs leading-snug text-muted">{opt.description}</p>
              </div>
            </label>
          );
        })}
      </div>

      {children ? <div className="border-t border-line p-4">{children}</div> : null}
    </section>
  );
}
